import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import type { PayableStage, PropertyType, ReferralStage } from '@valutten/shared-schemas/dist/partnerNetwork';
import { functions } from '../lib/firebase';
import { referralErrorMessage } from '../utils/partnerReferrals';
import type { PartnerReferralView } from './usePartnerReferrals';

/**
 * Referrer portal inside VALUTTEN: per-partner summary, one referral with its
 * stage history, and the disclosure register. Loyalty-codebase callables
 * (services/loyalty-functions/src/partners/partnerTenantPortal.ts), called by
 * name. Money is integer cents, times are ISO strings or null. The server
 * nulls every fee figure for hideCommissions callers; the UI hides them too.
 */

// ─── tenantGetPartnerSummary ─────────────────────────────────────────────────

export interface PartnerPublicProfile {
  partnerId: string;
  name: string;
  shortName: string;
  logoUrl?: string;
  brandColor: string;
  tagline: string;
  pillars: { title: string; body: string }[];
  servicePromise: string[];
  firstContactBusinessHours: number | null;
  acceptsApplications: boolean;
}

export interface PayoutBandRow {
  /** Inclusive lower bound of the purchase price, cents. */
  fromCents: number;
  /** Inclusive upper bound; null = and above. */
  upToCents: number | null;
  payoutIncGstCents: number;
  /** The same band for a loaded property type; null when no loading applies to payouts. */
  loadedPayoutIncGstCents: number | null;
}

export interface PayoutStageCard {
  stage: PayableStage;
  label: string;
  /** Raw payout rule, ex GST. Rendered only through `description`. */
  rule: unknown;
  /** Set when the rule is fixed-only. */
  flatIncGstCents: number | null;
  /** Set when the rule is one band table on the purchase price. */
  bands: PayoutBandRow[] | null;
  /** Plain-English line, always present (a share-of-partner-fee rule has only this). */
  description: string;
}

/** What the REFERRER is paid. Never contains the partner's own fee. */
export interface PayoutCard {
  scheduleVersion: number;
  gstRateBps: number;
  loading: { rateBps: number; propertyTypes: PropertyType[] } | null;
  stages: PayoutStageCard[];
}

export interface PartnerSummaryEntry {
  partner: PartnerPublicProfile;
  payoutCard: PayoutCard | null;
  totals: {
    referred: number;
    byStage: Record<ReferralStage, number>;
    settled: number;
    /** Every ledger entry, owed or paid. */
    feesEarnedIncGstCents: number | null;
    feesOwedIncGstCents: number | null;
  };
  nextPayment: {
    amountIncGstCents: number;
    count: number;
    expectedAt: string | null;
    entries: { entryId: string; referralId: string; clientName: string | null; stage: string; amountIncGstCents: number }[];
  } | null;
  /** Up to 10 moves, newest first. */
  recent: { referralId: string; clientName: string; to: ReferralStage; at: string | null; feeIncGstCents: number | null }[];
  /** One point per ledger entry, oldest first, cumulative. */
  incomeSeries: { at: string | null; cumulativeIncGstCents: number }[] | null;
}

export interface PartnerSummaryResponse {
  partners: PartnerSummaryEntry[];
  truncated: boolean;
}

// ─── tenantGetPartnerReferral ────────────────────────────────────────────────

/** A stage move. Desk notes and actors are internal to the partner and never sent. */
export interface ReferralEvent {
  from: ReferralStage | null;
  to: ReferralStage;
  at: string | null;
}

export interface ReferralDetail {
  referral: PartnerReferralView;
  events: ReferralEvent[];
}

// ─── tenantGetDisclosureRegister (view only; never leaves the app) ───────────

/** One register row: flat primitives only. */
export interface DisclosureRow {
  referralId: string;
  clientName: string;
  broker: string | null;
  brokerDocId: string | null;
  partnerId: string;
  partner: string;
  submittedAt: string | null;
  clientConsentAt: string | null;
  feeDisclosureAt: string | null;
  /** Named only when the submitter is a user of the caller's company. */
  submittedBy: string | null;
  stage: ReferralStage;
  /** The fee card version the referral was priced under; null until priced. */
  feeScheduleVersion: number | null;
  settlementFeeIncGstCents: number | null;
}

export interface DisclosureRegisterResponse {
  rows: DisclosureRow[];
  /** Set past 5000 referrals. */
  truncated: boolean;
  from: string | null;
  to: string | null;
}

/** Brisbane calendar days, `to` inclusive. */
export interface DisclosureRange {
  from?: string;
  to?: string;
}

// ─── Plumbing ────────────────────────────────────────────────────────────────

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Callable error code without the `functions/` prefix, for callers that branch on it. */
  errorCode: string | null;
  reload: () => Promise<void>;
}

function useCallableOnce<Req, Res>(name: string, request: Req | null): AsyncState<Res> {
  const [data, setData] = useState<Res | null>(null);
  const [loading, setLoading] = useState(request !== null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const key = request === null ? null : JSON.stringify(request);

  const reload = useCallback(async () => {
    if (key === null) return;
    setLoading(true);
    try {
      const fn = httpsCallable<Req, Res>(functions, name);
      const res = await fn(JSON.parse(key) as Req);
      setData(res.data ?? null);
      setError(null);
      setErrorCode(null);
    } catch (err) {
      const code = (err as { code?: unknown })?.code;
      setErrorCode(typeof code === 'string' ? code.replace(/^functions\//, '') : 'unknown');
      setError(referralErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [name, key]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, loading, error, errorCode, reload };
}

export function usePartnerSummary(): AsyncState<PartnerSummaryResponse> {
  return useCallableOnce<Record<string, never>, PartnerSummaryResponse>('tenantGetPartnerSummary', {});
}

export function usePartnerReferralDetail(referralId: string | undefined): AsyncState<ReferralDetail> {
  return useCallableOnce<{ referralId: string }, ReferralDetail>(
    'tenantGetPartnerReferral',
    referralId ? { referralId } : null
  );
}

export function useDisclosureRegister(range: DisclosureRange): AsyncState<DisclosureRegisterResponse> {
  const request: DisclosureRange = {};
  if (range.from) request.from = range.from;
  if (range.to) request.to = range.to;
  return useCallableOnce<DisclosureRange, DisclosureRegisterResponse>('tenantGetDisclosureRegister', request);
}
